<template>
  <div class="catalog-page">
    <!-- Header -->
    <header class="catalog-header">
      <div class="catalog-header-content">
        <div class="catalog-logo-wrapper" v-if="logoUrl">
          <img :src="logoUrl" alt="Logo" class="catalog-logo" />
        </div>
        <p v-if="sellerName" class="catalog-seller">Vendedor: {{ sellerName }}</p>
      </div>
      <div class="catalog-search-wrapper">
        <input
          v-model="search"
          @input="onSearch"
          type="text"
          class="catalog-search"
          placeholder="Buscar producto..."
        />
      </div>
    </header>

    <!-- Productos -->
    <main class="catalog-main">
      <div v-if="loadingProducts" class="catalog-loading">
        <i class="fas fa-spinner fa-spin fa-2x"></i>
        <p>Cargando productos...</p>
      </div>

      <div v-else-if="products.length === 0" class="catalog-empty">
        <p>No se encontraron productos.</p>
      </div>

      <div v-else class="catalog-grid">
        <div v-for="p in products" :key="p.id" class="catalog-card" @click="addToCart(p)">
          <div class="catalog-card-img">
            <img v-if="p.image_url && !p.imgError" :src="p.image_url" :alt="p.name" @error="p.imgError = true" />
            <i v-else class="fas fa-box catalog-img-placeholder"></i>
          </div>
          <div class="catalog-card-info">
            <h3 class="catalog-card-name">{{ p.name }}</h3>
            <p class="catalog-card-ref" v-if="p.ref">{{ p.ref }}</p>
            <p class="catalog-card-price">${{ formatPrice(p.sale_price) }}</p>
          </div>
          <div class="catalog-card-add">
            <i class="fas fa-plus-circle"></i>
          </div>
        </div>
      </div>

      <!-- Paginación -->
      <div v-if="pagination.lastPage > 1" class="catalog-pagination">
        <button :disabled="pagination.currentPage <= 1" @click="loadProducts(pagination.currentPage - 1)">Anterior</button>
        <span>{{ pagination.currentPage }} / {{ pagination.lastPage }}</span>
        <button :disabled="pagination.currentPage >= pagination.lastPage" @click="loadProducts(pagination.currentPage + 1)">Siguiente</button>
      </div>
    </main>

    <!-- Modal de selección de variante -->
    <div v-if="showVariantModal" class="catalog-modal-overlay" @click.self="showVariantModal = false">
      <div class="catalog-modal">
        <h3>Selecciona una variante</h3>
        <p class="catalog-modal-product">{{ pendingProduct?.name }}</p>
        <div class="catalog-variant-list">
          <div v-for="v in pendingProduct?.variants" :key="v.id" class="catalog-variant-row" @click="selectVariant(v)">
            <span>{{ v.title }}</span>
            <span class="catalog-variant-price">${{ formatPrice(v.price) }}</span>
          </div>
        </div>
        <button class="catalog-modal-close" @click="showVariantModal = false">Cancelar</button>
      </div>
    </div>

    <!-- Carrito flotante -->
    <div class="catalog-cart-fab" @click="showCheckout = true">
      <i class="fas fa-shopping-cart"></i>
      <span v-if="cartCount > 0" class="catalog-cart-badge">{{ cartCount }}</span>
    </div>

    <!-- Checkout lateral -->
    <transition name="slide">
      <div v-if="showCheckout" class="catalog-checkout-overlay" @click.self="showCheckout = false">
        <div class="catalog-checkout-panel">
          <div class="catalog-checkout-header">
            <h2>Resumen del pedido</h2>
            <button class="catalog-checkout-close" @click="showCheckout = false"><i class="fas fa-times"></i></button>
          </div>

          <div class="catalog-checkout-items" v-if="cart.length > 0">
            <div v-for="item in cart" :key="item.key" class="catalog-checkout-item">
              <div class="catalog-checkout-item-info">
                <h4>{{ item.name }}</h4>
                <p v-if="item.variantTitle">{{ item.variantTitle }}</p>
                <p class="catalog-checkout-item-price">${{ formatPrice(item.price) }} c/u</p>
              </div>
              <div class="catalog-checkout-item-qty">
                <button @click="changeQty(item, -1)">-</button>
                <span>{{ item.qty }}</span>
                <button @click="changeQty(item, 1)">+</button>
              </div>
              <div class="catalog-checkout-item-total">
                ${{ formatPrice(item.price * item.qty) }}
              </div>
              <button class="catalog-checkout-item-remove" @click="removeItem(item)">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
          <div v-else class="catalog-checkout-empty">
            <i class="fas fa-shopping-cart fa-3x"></i>
            <p>Tu carrito está vacío</p>
          </div>

          <div class="catalog-checkout-total" v-if="cart.length > 0">
            <span>Total:</span>
            <span class="catalog-checkout-total-value">${{ formatPrice(cartTotal) }}</span>
          </div>

          <!-- Formulario de cliente -->
          <div class="catalog-checkout-form" v-if="cart.length > 0">
            <h3>Datos de contacto</h3>
            <input v-model="form.name" type="text" placeholder="Nombre completo *" />
            <input v-model="form.document" type="text" placeholder="Cédula *" />
            <input v-model="form.phone" type="tel" placeholder="Teléfono *" />
            <input v-model="form.address" type="text" placeholder="Dirección *" />
            <input v-model="form.department" type="text" placeholder="Departamento *" />
            <input v-model="form.municipality" type="text" placeholder="Municipio *" />
            <textarea v-model="form.notes" placeholder="Notas (opcional)" rows="2"></textarea>

            <button class="catalog-checkout-submit" @click="sendOrder" :disabled="sending">
              <i v-if="sending" class="fas fa-spinner fa-spin"></i>
              <template v-else><i class="fab fa-whatsapp"></i> Enviar pedido por WhatsApp</template>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import catalogService from '@/services/catalog.service';

export default {
  name: 'PublicCatalog',
  data() {
    return {
      token: '',
      sellerName: '',
      sellerPhone: '',
      logoUrl: '',
      products: [],
      loadingProducts: true,
      search: '',
      searchTimer: null,
      pagination: { currentPage: 1, lastPage: 1, total: 0 },
      cart: [],
      showCheckout: false,
      showVariantModal: false,
      pendingProduct: null,
      sending: false,
      form: {
        name: '',
        document: '',
        phone: '',
        address: '',
        department: '',
        municipality: '',
        notes: '',
      },
    };
  },
  computed: {
    cartCount() {
      return this.cart.reduce((s, i) => s + i.qty, 0);
    },
    cartTotal() {
      return this.cart.reduce((s, i) => s + i.price * i.qty, 0);
    },
  },
  methods: {
    formatPrice(val) {
      return parseFloat(val || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadProducts(1), 400);
    },
    async loadProducts(page = 1) {
      this.loadingProducts = true;
      try {
        const params = { page, per_page: 12 };
        if (this.search) params.search = this.search;
        const res = await catalogService.getProducts(params);
        const d = res.data;
        this.products = (d.data || []).map(p => ({ ...p, imgError: false }));
        this.pagination = {
          currentPage: d.current_page || 1,
          lastPage: d.last_page || 1,
          total: d.total || 0,
        };
      } catch (e) {
        console.error('Error loading products:', e);
      } finally {
        this.loadingProducts = false;
      }
    },
    async loadSellerInfo() {
      try {
        const res = await catalogService.getSellerInfo(this.token);
        this.sellerName = res.data.seller_name || '';
        this.sellerPhone = res.data.seller_phone || '';
        const logoPath = res.data.logo_path;
        if (logoPath) {
          const base = process.env.VUE_APP_API_IMAGE_URL || '';
          this.logoUrl = base + logoPath;
        }
      } catch (e) {
        console.error('Error loading seller info:', e);
      }
    },
    addToCart(product) {
      if (product.has_variants && product.variants && product.variants.length > 0) {
        this.pendingProduct = product;
        this.showVariantModal = true;
        return;
      }
      this.pushToCart(product, null);
    },
    selectVariant(variant) {
      this.pushToCart(this.pendingProduct, variant);
      this.showVariantModal = false;
      this.pendingProduct = null;
    },
    pushToCart(product, variant) {
      const key = variant ? `${product.id}_${variant.id}` : `${product.id}`;
      const existing = this.cart.find(i => i.key === key);
      if (existing) {
        existing.qty++;
      } else {
        this.cart.push({
          key,
          productId: product.id,
          name: product.name,
          variantId: variant ? variant.id : null,
          variantTitle: variant ? variant.title : '',
          price: variant ? parseFloat(variant.price) : parseFloat(product.sale_price),
          qty: 1,
        });
      }
    },
    changeQty(item, delta) {
      item.qty += delta;
      if (item.qty <= 0) {
        this.cart = this.cart.filter(i => i.key !== item.key);
      }
    },
    removeItem(item) {
      this.cart = this.cart.filter(i => i.key !== item.key);
    },
    sendOrder() {
      if (!this.form.name || !this.form.phone || !this.form.address) {
        alert('Por favor completa al menos nombre, teléfono y dirección.');
        return;
      }
      this.sending = true;

      const line = '─'.repeat(28);
      let msg = '';
      msg += `*PEDIDO DE CATÁLOGO*\n`;
      msg += `${line}\n`;
      msg += `Vendedor: ${this.sellerName || 'N/A'}\n`;
      msg += `Fecha: ${new Date().toLocaleString('es-CO')}\n`;
      msg += `${line}\n\n`;
      msg += `*PRODUCTOS*\n`;
      msg += `${line}\n`;
      this.cart.forEach((item, idx) => {
        msg += `${idx + 1}. ${item.name}\n`;
        if (item.variantTitle) msg += `   Variante: ${item.variantTitle}\n`;
        msg += `   Cant: ${item.qty} x $${this.formatPrice(item.price)}\n`;
        msg += `   Subtotal: $${this.formatPrice(item.price * item.qty)}\n`;
        if (idx < this.cart.length - 1) msg += `\n`;
      });
      msg += `${line}\n`;
      msg += `*TOTAL: $${this.formatPrice(this.cartTotal)}*\n`;
      msg += `${line}\n\n`;
      msg += `*DATOS DEL CLIENTE*\n`;
      msg += `${line}\n`;
      msg += `Nombre: ${this.form.name}\n`;
      if (this.form.document) msg += `Cédula: ${this.form.document}\n`;
      msg += `Teléfono: ${this.form.phone}\n`;
      msg += `Dirección: ${this.form.address}\n`;
      if (this.form.department) msg += `Departamento: ${this.form.department}\n`;
      if (this.form.municipality) msg += `Municipio: ${this.form.municipality}\n`;
      if (this.form.notes) {
        msg += `${line}\n`;
        msg += `*NOTAS*\n`;
        msg += `${this.form.notes}\n`;
      }
      msg += `${line}\n`;
      msg += `¡Gracias! 🛍️`;

      const phone = this.sellerPhone ? this.sellerPhone.replace(/\D/g, '') : '';
      const waNumber = phone.startsWith('57') ? phone : '57' + phone;
      const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`;

      window.open(url, '_blank');
      this.sending = false;
    },
  },
  mounted() {
    this.token = this.$route.params.token;
    if (this.token) {
      this.loadSellerInfo();
    }
    this.loadProducts();
  },
};
</script>

<style scoped>
.catalog-page {
  font-family: 'Inter', Arial, Helvetica, sans-serif;
  background: #f5f5f5;
  min-height: 100vh;
  padding-bottom: 80px;
}

/* Header */
.catalog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: 20px 16px;
  position: sticky;
  top: 0;
  z-index: 100;
}
.catalog-header-content {
  max-width: 600px;
  margin: 0 auto 12px;
  text-align: center;
}
.catalog-logo-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}
.catalog-logo {
  max-height: 70px;
  max-width: 200px;
  object-fit: contain;
  border-radius: 8px;
}
.catalog-title {
  font-size: 24px;
  margin: 0;
  font-weight: 700;
}
.catalog-seller {
  font-size: 13px;
  margin: 4px 0 0;
  opacity: 0.9;
}
.catalog-search-wrapper {
  max-width: 600px;
  margin: 0 auto;
}
.catalog-search {
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-radius: 25px;
  font-size: 15px;
  outline: none;
}

/* Productos */
.catalog-main {
  max-width: 600px;
  margin: 0 auto;
  padding: 16px;
}
.catalog-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
.catalog-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  cursor: pointer;
  position: relative;
  transition: transform 0.15s;
}
.catalog-card:active {
  transform: scale(0.97);
}
.catalog-card-img {
  width: 100%;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  overflow: hidden;
}
.catalog-card-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.catalog-img-placeholder {
  font-size: 40px;
  color: #ccc;
}
.catalog-card-info {
  padding: 10px 12px;
}
.catalog-card-name {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.catalog-card-ref {
  font-size: 11px;
  color: #999;
  margin: 0 0 4px;
}
.catalog-card-price {
  font-size: 16px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}
.catalog-card-add {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: #48bb78;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

/* Loading / empty */
.catalog-loading, .catalog-empty {
  text-align: center;
  padding: 40px;
  color: #999;
}

/* Paginación */
.catalog-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
}
.catalog-pagination button {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
  cursor: pointer;
  font-size: 14px;
}
.catalog-pagination button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Modal variante */
.catalog-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.catalog-modal {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  width: 100%;
  max-width: 400px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}
.catalog-modal h3 {
  margin: 0 0 8px;
  font-size: 18px;
  flex-shrink: 0;
}
.catalog-modal-product {
  font-size: 14px;
  color: #666;
  margin: 0 0 16px;
  flex-shrink: 0;
}
.catalog-variant-list {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
.catalog-variant-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border: 1px solid #eee;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
}
.catalog-variant-row:active {
  background: #f0f0f0;
}
.catalog-variant-price {
  font-weight: 700;
}
.catalog-modal-close {
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #eee;
  margin-top: 8px;
  cursor: pointer;
  flex-shrink: 0;
}

/* Carrito FAB */
.catalog-cart-fab {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #48bb78;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.5);
  cursor: pointer;
  z-index: 150;
}
.catalog-cart-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #e53e3e;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

/* Checkout */
.catalog-checkout-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.4);
  z-index: 300;
}
.catalog-checkout-panel {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  max-width: 420px;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}
.catalog-checkout-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
.catalog-checkout-header h2 {
  font-size: 18px;
  margin: 0;
}
.catalog-checkout-close {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #999;
}
.catalog-checkout-items {
  padding: 12px 20px;
  flex: 1;
}
.catalog-checkout-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}
.catalog-checkout-item-info {
  flex: 1;
}
.catalog-checkout-item-info h4 {
  font-size: 14px;
  margin: 0;
}
.catalog-checkout-item-info p {
  font-size: 12px;
  color: #999;
  margin: 0;
}
.catalog-checkout-item-price {
  font-size: 11px !important;
}
.catalog-checkout-item-qty {
  display: flex;
  align-items: center;
  gap: 8px;
}
.catalog-checkout-item-qty button {
  width: 28px;
  height: 28px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 16px;
}
.catalog-checkout-item-qty span {
  font-weight: 600;
  min-width: 20px;
  text-align: center;
}
.catalog-checkout-item-total {
  font-weight: 700;
  font-size: 14px;
  min-width: 70px;
  text-align: right;
}
.catalog-checkout-item-remove {
  border: none;
  background: none;
  color: #e53e3e;
  cursor: pointer;
  font-size: 14px;
}
.catalog-checkout-empty {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}
.catalog-checkout-total {
  padding: 16px 20px;
  border-top: 2px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
}
.catalog-checkout-total-value {
  color: #48bb78;
}

/* Form */
.catalog-checkout-form {
  padding: 16px 20px;
  border-top: 1px solid #eee;
}
.catalog-checkout-form h3 {
  font-size: 16px;
  margin: 0 0 12px;
}
.catalog-checkout-form input,
.catalog-checkout-form textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 10px;
  outline: none;
}
.catalog-checkout-form input:focus,
.catalog-checkout-form textarea:focus {
  border-color: #48bb78;
}
.catalog-checkout-submit {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: #25d366;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.catalog-checkout-submit:disabled {
  opacity: 0.6;
}

/* Slide transition */
.slide-enter-active, .slide-leave-active {
  transition: opacity 0.3s;
}
.slide-enter-active .catalog-checkout-panel,
.slide-leave-active .catalog-checkout-panel {
  transition: transform 0.3s;
}
.slide-enter-from .catalog-checkout-panel,
.slide-leave-to .catalog-checkout-panel {
  transform: translateX(100%);
}
</style>
